import React, {useContext, useEffect} from 'react'
import {FormattedDate} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import {HighlightedTitle} from 'components/Home/HighlightedTitle'
import {useNavigateToSearchWithTrigger} from 'hooks/useNavigateToSearch'
import {getSearchContext} from 'middleware/analytics/selectors'
import {SearchTrigger} from 'modules/searchBox/types'
import {pick} from 'ramda'

import {Button} from '@vio/atlas/Button/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {roomsToString} from '@vio/core/src/room/business/roomConfiguration'
import {addDaysToDate, dateStringToMiddayDate} from '@vio/core/src/utils/date'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'
import {GuestAndRoomText} from '@vio/shared/src/search/GuestAndRoomsText'

import {SimilarHotels} from '../components/SimilarHotels'
import {
  DateAndRoomsText,
  SeeAllText,
  TopSection,
  TopSectionText
} from '../components/TopSection'
import {CarouselWidgetsContext} from '../context/CarouselWidgetsContext'
import {DESTINATION_KEYS, ITINERARY_KEYS} from '../utils/constants'
import {getFirstBookingWithInterval} from './utils'

export const ExtendYourStay = () => {
  const isHydrated = useIsHydrated()
  const {bookings, searchHistory} = useContext(CarouselWidgetsContext)
  const {isMobile} = useDeviceLayout()
  const dispatch = useDispatch()

  const navigateToSearch = useNavigateToSearchWithTrigger(
    SearchTrigger.ExtendYourStayWidget
  )

  const searchContext = useSelector(getSearchContext)

  const lastSearch = searchHistory?.[0]

  const bookingToExtend = getFirstBookingWithInterval(bookings)

  // Don't show Extend stay in case Recent searches anchor hotel is the same
  const shouldShowComponent =
    bookings?.length && bookingToExtend.hotelId !== lastSearch?.hotelId

  const trackingPayload = {
    component: 'StayLongerNearbyCarousel',
    team: Team.Retention
  }

  useEffect(() => {
    if (shouldShowComponent) {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.Carousel,
          action: Action.Displayed,
          ...trackingPayload,
          payload: {
            totalNumber: bookings?.length,
            searchItem: {
              ...pick(
                [...DESTINATION_KEYS, ...ITINERARY_KEYS],
                bookingToExtend
              ),
              rooms: roomsToString(bookingToExtend.rooms)
            }
          },
          analyticsContext: {
            [AnalyticsContext.SearchContext]: searchContext
          }
        })
      )
    }
  }, [shouldShowComponent, searchContext, dispatch])

  if (!isHydrated || !shouldShowComponent) {
    return null
  }
  const checkIn = bookingToExtend.checkOut
  const checkOut = addDaysToDate(checkIn, 1)
  const rooms = roomsToString(bookingToExtend.rooms)
  const baseSearchParameters = {
    rooms,
    checkIn,
    checkOut,
    userSearch: '1' as const,
    homeSearch: '1' as const
  }

  const onHotelClick = ({hotelId}: {hotelId: string}) =>
    // bookingHint=1 (show date confirmation tooltip) is used to open the search box for mobile and date confirmation tooltip on desktop
    // it was added as part of this task: https://app.shortcut.com/findhotel/story/154347/update-landing-experience-for-users-with-no-dates-default-dates-widget-traffic
    navigateToSearch({...baseSearchParameters, hotelId, bookingHint: '1'})

  const onSeeAllClick = () => {
    const {hotelId} = bookingToExtend

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.CarouselSeeAll,
        action: Action.Clicked,
        ...trackingPayload,
        payload: {
          totalNumber: bookings?.length,
          searchItem: {
            hotelId,
            checkIn,
            checkOut,
            rooms
          }
        }
      })
    )

    navigateToSearch({...baseSearchParameters, hotelId})
  }

  return (
    <div className="chromatic-ignore">
      <TopSection className={cx(isMobile && '--mobile')}>
        <TopSectionText>
          <Text variant="titleXL" dataId="ExtendYourStay">
            <HighlightedTitle source="ExtendYourStay" />
          </Text>

          <DateAndRoomsText variant="bodyS">
            <Text tag="span" variant="labelS">
              <FormattedDate
                value={dateStringToMiddayDate(checkIn)}
                month="short"
                day="2-digit"
              />
              {' - '}
              <FormattedDate
                value={dateStringToMiddayDate(checkOut)}
                month="short"
                day="2-digit"
              />
            </Text>
            {' · '}
            <GuestAndRoomText rooms={rooms} />
          </DateAndRoomsText>
        </TopSectionText>
        {!isMobile && (
          <Button variant="tertiary" onClick={onSeeAllClick}>
            <SeeAllText>
              <FormattedMessageWrapper
                id="hotelCard.seeAll"
                defaultMessage="See all"
              />
            </SeeAllText>
          </Button>
        )}
      </TopSection>
      <SimilarHotels
        includeSameProperty
        hotelId={bookingToExtend.hotelId}
        checkIn={checkIn}
        checkOut={checkOut}
        rooms={rooms}
        trackingPayload={trackingPayload}
        onHotelClick={onHotelClick}
        hasSidePaddings={isMobile}
        parentId="extend-your-stay"
      />
      {isMobile && (
        <Box padding="s500" paddingBottom="s000">
          <Button variant="tertiary" fullWidth onClick={onSeeAllClick}>
            <SeeAllText>
              <FormattedMessageWrapper
                id="hotelCard.seeAll"
                defaultMessage="See all"
              />
            </SeeAllText>
          </Button>
        </Box>
      )}
    </div>
  )
}
